import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import Guid from "_shared/guids/guid";
import { createMRTColumnHelper, type MRT_TableOptions } from "material-react-table";
import React, { useCallback, useMemo } from "react";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderParameters,
  UserCoworkerOrderField,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import UserCoworker from "users/information/coworker-information/user-coworker";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import useInfiniteUserCoworkers from "users/information/coworker-information/_hooks/use-infinite-user-coworkers";

const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const CoworkerTableRoot = styled("div")(() => ({
  height: "100%",
  "& .UserCoworkers-tableContainer": {
    flex: 1,
  },
  "& .UserCoworkers-tablePaper": {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  "& .UserCoworkers-row": {
    cursor: "pointer",
  },
  "& .UserCoworkers-row--disabled": {
    cursor: "default",
    pointerEvents: "none",
  },
}));
const ErrorContent = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
}));

type UserCoworkersProps = {
  selectedMemberUserIds?: Guid[];
  onSelected: (rowData: UserCoworker) => void;
};

const columnHelper = createMRTColumnHelper<UserCoworker>();

export default function UserCoworkers(props: Readonly<UserCoworkersProps>) {
  const { selectedMemberUserIds, onSelected } = props;

  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const query = useMemo(
    () =>
      new UserCoworkerQuery(
        new UserCoworkerFilterParameters(),
        new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
        PaginationParameters.DEFAULT,
      ),
    [],
  );
  const { coworkers, isLoading, isInitialLoading, error, loadMore, retry } = useInfiniteUserCoworkers(query);
  useInfiniteScroll(loadMore, coworkers.hasNextPage, isLoading, tableContainerRef);

  const renderNameColumn = useCallback((userCoworker: UserCoworker) => {
    if (!userCoworker.name) return <Icon>remove</Icon>;
    return (
      <NameCellContainer>
        <IndividualAvatar
          individual={userCoworker.asIndividual()}
        />
        <RowTitle>{userCoworker.name.fullName}</RowTitle>
      </NameCellContainer>
    );
  }, []);

  const renderEmailColumn = useCallback((userCoworker: UserCoworker) => {
    if (!userCoworker.email) return <Icon>remove</Icon>;
    const email = userCoworker.email.toString();
    return <Link href={`mailto:${email}`}>{email}</Link>;
  }, []);

  const columns = useMemo(() => [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor(renderEmailColumn, {
      header: "Email",
      id: "email",
    }),
  ], [renderNameColumn, renderEmailColumn]);

  const tableOptions: MRT_TableOptions<UserCoworker> = {
    columns,
    data: coworkers.data,
    // A picker: the row is a choice being made, not a record to act on.
    enableRowActions: false,
    enableRowSelection: false,
    enableTableHead: true,
    enableBottomToolbar: false,
    enablePagination: false,
    muiTableBodyRowProps: ({ row }) => {
      const disabled = selectedMemberUserIds?.some((id) => row.original?.userId && id.isEqualTo(row.original.userId));
      return {
        onClick: disabled ? undefined : () => onSelected(row.original),
        className: disabled ? "UserCoworkers-row UserCoworkers-row--disabled" : "UserCoworkers-row",
      };
    },
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: error
      ? {
        color: "error",
        children: (
          <ErrorContent>
            Error loading data
            <Button color="inherit" size="small" onClick={retry}>Retry</Button>
          </ErrorContent>
        ),
      }
      : undefined,
    muiTableContainerProps: { className: "UserCoworkers-tableContainer", ref: tableContainerRef },
    muiTablePaperProps: { className: "UserCoworkers-tablePaper" },
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: Boolean(error),
      isLoading: isInitialLoading,
      onRetry: retry,
      subject: "coworkers",
    }),
    state: {
      isLoading: isInitialLoading,
      showAlertBanner: Boolean(error),
      showProgressBars: isLoading && !isInitialLoading,
    },
  }

  return (
    <CoworkerTableRoot>
      <MRTNoMemo<UserCoworker> options={tableOptions} />
    </CoworkerTableRoot>
  );
}
